import axios from 'axios';
import { BASE_URL } from 'constants/api';
import {
  ContentThumbnailOverride,
  CreateThumbnailOverrideRequest,
  UpdateThumbnailOverrideRequest,
} from '../types/thumbnailOverrideType';

class ThumbnailOverrideService {
  private getBasePath(contentId: number): string {
    return `${BASE_URL}/api/v1/content/${contentId}/thumbnail-override/`;
  }

  /**
   * Fetch Override thumbnail list
   */
  async fetchList(contentId: number): Promise<ContentThumbnailOverride[]> {
    const response = await axios.get(this.getBasePath(contentId));
    // Return directly if API response is array, use results field if object
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // Handle paginated response
    if (response.data && Array.isArray(response.data.results)) {
      return response.data.results;
    }
    return [];
  }

  /**
   * Fetch Override thumbnail detail
   */
  async fetchById(contentId: number, thumbnailId: number): Promise<ContentThumbnailOverride> {
    const response = await axios.get(`${this.getBasePath(contentId)}${thumbnailId}/`);
    return response.data;
  }

  /**
   * Create Override thumbnail (file upload)
   */
  async createWithFile(
    contentId: number,
    file: File,
    language: string,
    orientation: string = 'landscape'
  ): Promise<ContentThumbnailOverride> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    formData.append('orientation', orientation);

    const response = await axios.post(this.getBasePath(contentId), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Create Override thumbnail (external URL)
   */
  async createWithUrl(
    contentId: number,
    request: CreateThumbnailOverrideRequest
  ): Promise<ContentThumbnailOverride> {
    const response = await axios.post(this.getBasePath(contentId), {
      external_url: request.external_url,
      language: request.language,
      orientation: request.orientation || 'landscape',
      width: request.width,
      height: request.height,
    });
    return response.data;
  }

  /**
   * Update Override thumbnail (file)
   */
  async updateWithFile(
    contentId: number,
    thumbnailId: number,
    file: File,
    orientation?: string
  ): Promise<ContentThumbnailOverride> {
    const formData = new FormData();
    formData.append('file', file);
    if (orientation) {
      formData.append('orientation', orientation);
    }

    const response = await axios.patch(`${this.getBasePath(contentId)}${thumbnailId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Update Override thumbnail (URL)
   */
  async updateWithUrl(
    contentId: number,
    thumbnailId: number,
    request: UpdateThumbnailOverrideRequest
  ): Promise<ContentThumbnailOverride> {
    const response = await axios.patch(`${this.getBasePath(contentId)}${thumbnailId}/`, {
      external_url: request.external_url,
      orientation: request.orientation,
      width: request.width,
      height: request.height,
    });
    return response.data;
  }

  /**
   * Delete Override thumbnail
   */
  async delete(contentId: number, thumbnailId: number): Promise<{ message: string }> {
    const response = await axios.delete(`${this.getBasePath(contentId)}${thumbnailId}/`);
    return response.data;
  }
}

export const thumbnailOverrideService = new ThumbnailOverrideService();
